import React, { useState } from 'react'
import {
  Box,
  Typography,
  Divider,
  Button,
  TextField,
  IconButton
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface QuoteSummaryProps {
  quoteId: string
  currentQuoteDetails: {
    subTotal: string
    tax: string
    assemblyFee: string
    totalPrice: string
  }
  poNumber: string
  onUpdatePoNumber: (po: string) => void
  onSwitchToCart: (quoteId: string) => void
}

const QuoteSummary = ({
  quoteId,
  currentQuoteDetails,
  poNumber,
  onUpdatePoNumber,
  onSwitchToCart
}: QuoteSummaryProps) => {
  const { t } = useTranslation()
  const [editingPoNumber, setEditingPoNumber] = useState(false)
  const { register, handleSubmit, reset } = useForm<{ poNumber: string }>({
    defaultValues: { poNumber: poNumber || '' }
  })

  const handleStartEditPoNumber = () => {
    setEditingPoNumber(true)
    reset({ poNumber: poNumber || '' })
  }

  const handleCancelEditPoNumber = () => {
    setEditingPoNumber(false)
    reset({ poNumber: poNumber || '' })
  }

  const onSubmitPoNumber = (data: { poNumber: string }) => {
    onUpdatePoNumber(data.poNumber)
    setEditingPoNumber(false)
  }

  return (
    <Box
      sx={{
        bgcolor: '#F8F8F8',
        borderRadius: 2,
        width: '350px',
        height: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '30px',
        mb: 5
      }}
    >
      <Typography variant='h3'>{t('quote.summary.title')}</Typography>

      <Divider sx={{ my: 2 }} />

      <Box display='flex' justifyContent='space-between' sx={{ my: 1 }}>
        <Typography variant='body1'>{t('quote.summary.subtotal')}</Typography>
        <Typography variant='body1'>${currentQuoteDetails.subTotal}</Typography>
      </Box>

      <Box display='flex' justifyContent='space-between' sx={{ my: 1 }}>
        <Typography variant='body1'>{t('quote.summary.tax')}</Typography>
        <Typography variant='body1'>${currentQuoteDetails.tax}</Typography>
      </Box>

      <Box display='flex' justifyContent='space-between' sx={{ my: 1 }}>
        <Typography variant='body1'>
          {t('quote.summary.assemblyFee')}
        </Typography>
        <Typography variant='body1'>
          ${currentQuoteDetails.assemblyFee}
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box display='flex' justifyContent='space-between' sx={{ my: 1 }}>
        <Typography variant='h2'>{t('quote.summary.total')}</Typography>
        <Typography variant='h2'>${currentQuoteDetails.totalPrice}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box display='flex' alignItems='center' sx={{ my: 1 }}>
        {editingPoNumber ? (
          <form onSubmit={handleSubmit(onSubmitPoNumber)}>
            <Box display='flex' alignItems='center' gap={1}>
              <TextField
                size='small'
                label={t('quote.poNumber')}
                {...register('poNumber')}
                inputProps={{ maxLength: 20 }}
              />
              <IconButton color='primary' type='submit'>
                <CheckIcon />
              </IconButton>
              <IconButton color='inherit' onClick={handleCancelEditPoNumber}>
                <CloseIcon />
              </IconButton>
            </Box>
          </form>
        ) : (
          <>
            <Typography variant='body1'>
              {t('quote.poNumber')}: {poNumber || '___'}
            </Typography>
            <IconButton size='small' onClick={handleStartEditPoNumber}>
              <EditIcon />
            </IconButton>
          </>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box
        sx={{
          mt: 5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Button
          variant='outlined'
          disabled={!quoteId}
          sx={{
            mb: 2,
            borderColor: 'primary.main',
            borderRadius: '4px',
            borderWidth: '1px',
            width: '290px',
            height: '46px'
          }}
          onClick={() => onSwitchToCart(quoteId)}
        >
          {t('quote.summary.moveToCart')}
        </Button>
      </Box>
    </Box>
  )
}

export default QuoteSummary
